import React, { useEffect, useMemo, useState } from "react"
import { Button, Checkbox, Modal, Segmented, Select } from "antd"
import { describeDataVariable } from "../../model/dataVariables"
import { dataTablesFor, defaultTablesFor, gridTablesFor } from "../../model/defaults"
import { defaultScores } from "../../model/scale"
import { shownNames } from "../../model/shownLabels"
import { canSummarise, legacySummary, sameSummary, summaryFits } from "../../model/summaries"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import { CatalogueQuestion, findQuestion } from "../../types/catalogue"
import type { SummaryChoice, TableSpec, TabSpecDefaults } from "../../types/tabSpecDocument"
import { SPEC_DIALOG_Z_INDEX } from "../Header/SpecHeader.component"
import SummaryCheckboxes, { SUMMARY_OPTIONS } from "../shared/SummaryCheckboxes.component"
import { dataNotReady } from "./dataState"

type Source = "questionnaire" | "data"

const SOURCES: { value: Source; label: string }[] = [
  { value: "questionnaire", label: "From the questionnaire" },
  { value: "data", label: "From the data" },
]

const QUESTIONNAIRE_HINT =
  "The table gets the defaults of the spec, like the tables built from the questionnaire."
const DATA_HINT =
  "Variables in the prepared data that are not in the questionnaire: made by the preparation, or in the data file itself."
/** The same in a project without a questionnaire: every variable of the data is offered. */
const DATA_ONLY_HINT =
  "Variables of the prepared data: in the data file itself, or made by the preparation."
const NO_VARIABLES = "The prepared data has no variable outside the questionnaire."
const NO_DATA_ONLY_VARIABLES = "The prepared data has no variable that can be made into a table."
/** A grid question whose summary is None and whose row tables are off in the defaults. */
const NO_TABLE =
  "The defaults give this grid no table: its summary is None and One table per row is off."
/** Nothing ticked for a grid or a loop. */
const NOTHING_CHOSEN = "Choose a summary or a table per row."
const SUMMARIES_HINT =
  "Each summary is a table with a row per grid row. A grid with columns, or asked in a loop, gets one per column or loop iteration."

/** The summaries and the tables per row Add makes of a grid or a loop, once they are changed. */
interface GridChoice {
  summaries: SummaryChoice[]
  perRow: boolean
}

/** What the defaults give `q`, as the checkboxes show it: its summaries, and whether it gets a table per row. */
const defaultChoiceOf = (q: CatalogueQuestion, d: TabSpecDefaults): GridChoice => {
  const made = defaultTablesFor(q, d, () => "")
  const summaries: SummaryChoice[] = []
  made.forEach((t) => {
    if (t.kind !== "grid-summary") return
    const summary = t.summary ?? legacySummary(q, d)
    if (!summaries.some((s) => sameSummary(s, summary))) summaries.push(summary)
  })
  return { summaries, perRow: made.some((t) => t.kind !== "grid-summary") }
}

/**
 * The summaries offered for `q`, those that suit it by its default scores
 * (`summaryFits`, the rule the defaults and the inspector follow too): the
 * mean and the sum of numbers; for answers with scores the boxes, the middle
 * and the mean, and NPS on a 0 to 10 scale.
 */
const summaryOptionsOf = (q: CatalogueQuestion): SummaryChoice[] => {
  const scores = defaultScores(q)
  return SUMMARY_OPTIONS.filter((c) => summaryFits(c, q, scores))
}

/** Whether one answer suits `q`: a question of answers, by its first. */
const offersOneAnswer = (q: CatalogueQuestion): boolean =>
  q.options.length > 0 && summaryFits({ kind: "answer", label: q.options[0].label }, q, null)

/** The tables Add makes for `q`, from the questionnaire or from the data. */
const tablesOf = (
  source: Source,
  q: CatalogueQuestion,
  defaults: TabSpecDefaults,
  makeId?: () => string
): TableSpec[] =>
  source === "data"
    ? dataTablesFor(q, defaults, makeId)
    : defaultTablesFor(q, defaults, makeId).map((t): TableSpec => ({ ...t, origin: "added" }))

/** The list opens inside the dialog, so it is inside the spec and gets its styles. */
const insideDialog = (trigger: HTMLElement): HTMLElement => trigger.parentElement ?? document.body

/** What a search of the list matches: the name shown, and the label when that differs. */
const searchOf = (shown: string, label: string): string =>
  shown === label ? label : `${shown}\n${label}`

/**
 * A variable of the data as the list names it: its shown label, or, without
 * one, its label, a variable nested in the codebook by its parts ("Brand:
 * Bank 1: score") rather than its path.
 */
const wordsOf = (label: string, shown: string): string =>
  shown === label ? label.split("//").join(": ") : shown

export interface AddTableDialogProps {
  open: boolean
  onClose: () => void
  /** Opens "Build tables from the data" in its place; without it the link is not shown. */
  onAddSeveral?: () => void
  /** Where the dialog renders; the tab spec's root, so it gets the --qc-* tokens. */
  getContainer?: () => HTMLElement
}

/**
 * Adds the tables of one question of the questionnaire, or of one variable
 * of the prepared data that is not in the questionnaire, with the spec's
 * defaults. The tables are added at the end, as tables the user added, and
 * the first is selected, which opens it in the inspector. Variables from the
 * data are known once the data is imported and prepared; the weight is not
 * offered. In a project without a questionnaire only the data is offered.
 * "Add several at once" goes on to "Build tables from the data".
 */
const AddTableDialog: React.FC<AddTableDialogProps> = ({
  open,
  onClose,
  onAddSeveral,
  getContainer,
}) => {
  const storeDataOnly = useTabSpecStore((s) => s.dataOnly)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const labels = useTabSpecStore((s) => s.document.labels)
  const defaults = useTabSpecStore((s) => s.document.defaults)
  const weight = useTabSpecStore((s) => s.document.data.weight)
  const status = useTabSpecStore((s) => s.session.status)
  const sessionError = useTabSpecStore((s) => s.session.error)
  const addTables = useTabSpecStore((s) => s.addTables)
  const select = useTabSpecStore((s) => s.select)
  const [picked, setPicked] = useState<Source>("questionnaire")
  const [chosen, setChosen] = useState<Record<Source, string | null>>({
    questionnaire: null,
    data: null,
  })
  // The summaries and tables per row of a grid or a loop, once changed; null: the defaults'.
  const [grid, setGrid] = useState<GridChoice | null>(null)

  // Each opening starts with nothing chosen; the source stays as it was.
  useEffect(() => {
    if (open) setChosen({ questionnaire: null, data: null })
  }, [open])
  // Another question starts from the defaults again.
  useEffect(() => setGrid(null), [chosen.questionnaire, open])

  const dataOnly = storeDataOnly || catalogue?.dataOnly === true
  // Without a questionnaire there is nothing to choose from but the data.
  const source: Source = dataOnly ? "data" : picked
  const variables = useMemo(
    () => (catalogue?.dataVariables ?? []).filter((v) => v.label !== weight),
    [catalogue, weight]
  )
  // Each question and variable as the tables show it; the tooltip adds what
  // the list does not show (the label itself, a question's text), and a
  // search finds the label too.
  const questionOptions = useMemo(() => {
    const names = shownNames(labels)
    return (catalogue?.questions ?? []).map((q) => {
      const shown = names.variable(q.label)
      return {
        value: q.label,
        label: shown,
        title: shown === q.label ? q.text || q.label : q.text ? `${q.label}. ${q.text}` : q.label,
        search: searchOf(shown, q.label),
      }
    })
  }, [catalogue, labels])
  const variableOptions = useMemo(() => {
    const names = shownNames(labels)
    return variables.map((v) => {
      const shown = wordsOf(v.label, names.variable(v.label))
      return {
        value: v.label,
        label: shown,
        title: v.label,
        search: searchOf(shown, v.label),
        phrase: describeDataVariable(v),
      }
    })
  }, [variables, labels])
  // Before the codebook is in, nothing is known about the data's variables.
  const notReady = dataNotReady(status, sessionError, catalogue)
  const dataKnown = notReady === null
  const dataMessage =
    notReady ??
    (variables.length === 0
      ? dataOnly
        ? NO_DATA_ONLY_VARIABLES
        : NO_VARIABLES
      : dataOnly
        ? DATA_ONLY_HINT
        : DATA_HINT)
  const addSeveral = (): void => {
    onClose()
    onAddSeveral?.()
  }

  const label = chosen[source]
  const question =
    !catalogue || label === null
      ? null
      : source === "data"
        ? (variables.find((v) => v.label === label) ?? null)
        : findQuestion(catalogue, label)
  // A grid or a loop of the questionnaire: which summaries, and a table per row.
  const summarised = source === "questionnaire" && question !== null && canSummarise(question)
  const gridChoice = summarised && question ? (grid ?? defaultChoiceOf(question, defaults)) : null
  /** The tables Add makes: the defaults', or those of the choices once they are changed. */
  const tablesFor = (q: CatalogueQuestion, makeId?: () => string): TableSpec[] =>
    summarised && grid
      ? gridTablesFor(q, defaults, grid, makeId).map((t): TableSpec => ({ ...t, origin: "added" }))
      : tablesOf(source, q, defaults, makeId)
  const makesNoTable = question !== null && tablesFor(question, () => "").length === 0
  const answers = question
    ? question.options.map((o) => ({
        value: o.label,
        label: shownNames(labels).option(question.label, o.label),
      }))
    : []

  const add = (): void => {
    if (!question) return
    const tables = tablesFor(question)
    if (tables.length === 0) return
    addTables(tables)
    select(tables[0].id)
    onClose()
  }
  const choose = (value: string): void => setChosen((c) => ({ ...c, [source]: value }))

  return (
    <Modal
      open={open}
      title="Add a table"
      okText="Add"
      okButtonProps={{ disabled: !question || makesNoTable }}
      onOk={add}
      onCancel={onClose}
      getContainer={getContainer}
      zIndex={SPEC_DIALOG_Z_INDEX}
      destroyOnHidden
    >
      <div className="ts-add-table">
        {!dataOnly && (
          <Segmented<Source>
            aria-label="Source"
            value={source}
            options={SOURCES}
            onChange={setPicked}
          />
        )}
        {source === "questionnaire" ? (
          <section className="ts-field" aria-label="Question">
            <Select
              showSearch
              aria-label="Question"
              className="ts-field__select"
              placeholder="Choose a question"
              value={chosen.questionnaire}
              options={questionOptions}
              optionFilterProp="search"
              notFoundContent="No question matches"
              disabled={!catalogue}
              getPopupContainer={insideDialog}
              onChange={choose}
            />
            <p className="ts-hint">
              {makesNoTable ? (grid ? NOTHING_CHOSEN : NO_TABLE) : QUESTIONNAIRE_HINT}
            </p>
            {question && gridChoice && (
              <section className="ts-field" aria-label="Summaries">
                <h3 className="ts-field__label">Summaries</h3>
                <SummaryCheckboxes
                  label="Summaries of the question"
                  options={summaryOptionsOf(question)}
                  value={gridChoice.summaries}
                  answers={offersOneAnswer(question) ? answers : undefined}
                  onChange={(summaries) => setGrid({ ...gridChoice, summaries })}
                />
                <p className="ts-hint">{SUMMARIES_HINT}</p>
                {/* The cells of a number grid have no table of their own. */}
                {question.type !== "grid-number" && (
                  <Checkbox
                    checked={gridChoice.perRow}
                    onChange={(e) => setGrid({ ...gridChoice, perRow: e.target.checked })}
                  >
                    A table per row
                  </Checkbox>
                )}
              </section>
            )}
          </section>
        ) : (
          <section className="ts-field" aria-label="Variable">
            <Select
              showSearch
              aria-label="Variable"
              className="ts-field__select"
              placeholder="Choose a variable"
              value={chosen.data}
              options={variableOptions}
              optionFilterProp="search"
              optionRender={(option) => (
                <>
                  {option.data.label}
                  <span className="ts-add-table__phrase">{option.data.phrase}</span>
                </>
              )}
              notFoundContent="No variable matches"
              disabled={!dataKnown || variables.length === 0}
              getPopupContainer={insideDialog}
              onChange={choose}
            />
            <p className="ts-hint">{dataMessage}</p>
            {onAddSeveral && dataKnown && variables.length > 0 && (
              <p className="ts-hint">
                <Button type="link" size="small" className="ts-link" onClick={addSeveral}>
                  Add several at once
                </Button>
              </p>
            )}
          </section>
        )}
      </div>
    </Modal>
  )
}

export default AddTableDialog
